// =============================================================================
// CONFIGURATION DES LISTES MÉTIER
// Permet d'ajouter/supprimer/modifier les valeurs des listes de l'application
// =============================================================================

import { useState, useEffect } from "react";
import { 
  List, Plus, Trash2, Save, X, Edit, GripVertical, 
  CheckCircle2, AlertTriangle, Palette, Tag, RefreshCw
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// LISTES GÉNÉRALES - Configuration Entreprise
// (Les listes liées aux aides sont dans Aides > Variables > Listes)
// =============================================================================
const LISTES_CONFIG = {
  equipes: {
    nom: "Équipes",
    description: "Équipes internes (poseurs, SAV, maintenance...)",
    icon: "👥",
    hasColor: true,
    collection: "op_equipes",
  },
  civilites: {
    nom: "Civilités",
    description: "Civilités disponibles (M., Mme, etc.)",
    icon: "👋",
  },
  sources_prospect: {
    nom: "Sources Prospect",
    description: "Origine des prospects (Publicité, Bouche-à-oreille, etc.)",
    icon: "📣",
  },
  motifs_annulation: {
    nom: "Motifs d'Annulation",
    description: "Raisons d'annulation d'un dossier",
    icon: "❌",
  },
  types_logement: {
    nom: "Types de Logement",
    description: "Maison, Appartement, etc.",
    icon: "🏠",
  },
  statuts_occupant: {
    nom: "Statuts Occupant",
    description: "Propriétaire, Locataire, etc.",
    icon: "🔑",
  },
  source_lead_types: {
    nom: "Sources de lead (dossier)",
    description: "Comment le client est arrivé : installateur, direct TEF, ANAH, recommandation, autre.",
    icon: "🎯",
  },
  // Note: statuts_mpr est géré dans le module Aides > Variables et Listes > Listes
};

const COULEURS_DISPONIBLES = [
  { code: "bg-zinc-500/20 border border-[var(--oh-border)]", label: "Gris" },
  { code: "bg-[var(--oh-accent-solid)]", label: "Bleu" },
  { code: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]", label: "Indigo" },
  { code: "bg-[var(--oh-accent-solid)]", label: "Violet" },
  { code: "bg-cyan-500/15 border border-cyan-500/30", label: "Cyan" },
  { code: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", label: "Teal" },
  { code: "bg-amber-500", label: "Ambre" },
  { code: "bg-orange-500", label: "Orange" },
  { code: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", label: "Vert" },
  { code: "bg-red-500", label: "Rouge" },
  { code: "bg-pink-500", label: "Rose" },
  { code: "bg-yellow-500", label: "Jaune" },
];

export default function ListesConfig({ tenantId }) {
  const [listes, setListes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeListe, setActiveListe] = useState("equipes");  // Par défaut sur équipes
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
    code: "",
    label: "",
    color: "bg-zinc-500/20 border border-[var(--oh-border)]",
    description: ""
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("session_token");
    return {
      "X-Tenant-ID": tenantId || "climatpac",
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  useEffect(() => {
    loadListes();
  }, [tenantId]);

  // Charger les équipes depuis l'API dédiée
  const loadEquipes = async () => {
    try {
      const res = await fetch(`${API}/api/operation/equipes`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        // Transformer les équipes au format des listes
        return (data.equipes || []).map(eq => ({
          id: eq.id,
          code: eq.nom.toLowerCase().replace(/\s+/g, '_').replace(/[éèê]/g, 'e'),
          label: eq.nom,
          color: tailwindToClass(eq.couleur),
          description: eq.description || ""
        }));
      }
    } catch (err) {
      console.error("Erreur chargement équipes:", err);
    }
    return [];
  };

  // Convertir couleur hex en classe Tailwind
  const tailwindToClass = (hexColor) => {
    const colorMap = {
      'var(--oh-stat-blue-text)': 'bg-[var(--oh-accent-solid)]',
      'var(--oh-stat-red-text)': 'bg-red-500',
      '#10B981': 'bg-green-500',
      'var(--oh-stat-amber-text)': 'bg-amber-500',
      '#8B5CF6': 'bg-[var(--oh-accent-solid)]',
      '#EC4899': 'bg-pink-500',
      '#14B8A6': 'bg-teal-500',
      'var(--oh-stat-orange-text)': 'bg-orange-500',
      'var(--oh-stat-cyan-text)': 'bg-cyan-500',
      'var(--oh-stat-yellow-text)': 'bg-yellow-500',
    };
    return colorMap[hexColor] || 'bg-[var(--oh-bg-elevated)]';
  };

  // Convertir classe Tailwind en hex
  const classToHex = (className) => {
    const colorMap = {
      'bg-[var(--oh-accent-solid)]': 'var(--oh-stat-blue-text)',
      'bg-red-500': 'var(--oh-stat-red-text)',
      'bg-green-500': '#10B981',
      'bg-amber-500': 'var(--oh-stat-amber-text)',
      'bg-[var(--oh-accent-solid)]': '#8B5CF6',
      'bg-pink-500': '#EC4899',
      'bg-teal-500': '#14B8A6',
      'bg-orange-500': 'var(--oh-stat-orange-text)',
      'bg-cyan-500': 'var(--oh-stat-cyan-text)',
      'bg-yellow-500': 'var(--oh-stat-yellow-text)',
      'bg-[var(--oh-bg-elevated)]': '#64748B',
    };
    return colorMap[className] || '#64748B';
  };

  const loadListes = async () => {
    setLoading(true);
    try {
      // Charger les listes métier standard
      const res = await fetch(`${API}/api/metier/documents-engine/admin/listes`, {
        headers: getAuthHeaders()
      });
      let listesData = {};
      if (res.ok) {
        const data = await res.json();
        listesData = data.listes || {};
      }
      
      // Charger les équipes séparément
      const equipes = await loadEquipes();
      listesData.equipes = equipes;
      
      setListes(listesData);
    } catch (err) {
      console.error("Erreur chargement listes:", err);
      toast.error("Erreur lors du chargement des listes");
    } finally {
      setLoading(false);
    }
  };

  const saveListe = async (listeCode, items) => {
    setSaving(true);
    try {
      // Pour les équipes, utiliser l'API dédiée
      if (listeCode === 'equipes') {
        // Les équipes sont gérées individuellement, pas en batch
        setListes(prev => ({ ...prev, [listeCode]: items }));
        toast.success("Équipes mises à jour");
        setSaving(false);
        return;
      }
      
      const res = await fetch(`${API}/api/metier/documents-engine/admin/listes/${listeCode}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ items })
      });
      if (res.ok) {
        setListes(prev => ({ ...prev, [listeCode]: items }));
        toast.success(`Liste "${LISTES_CONFIG[listeCode]?.nom}" enregistrée`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de l'enregistrement");
      }
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Ajouter une équipe via l'API
  const addEquipe = async (equipeData) => {
    try {
      const res = await fetch(
        `${API}/api/operation/equipes?nom=${encodeURIComponent(equipeData.label)}&description=${encodeURIComponent(equipeData.description || '')}&couleur=${encodeURIComponent(classToHex(equipeData.color))}`,
        {
          method: "POST",
          headers: getAuthHeaders()
        }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(`Équipe "${equipeData.label}" créée`);
        return data.id;
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur création équipe");
        return null;
      }
    } catch (err) {
      toast.error("Erreur création équipe");
      return null;
    }
  };

  // Supprimer une équipe via l'API
  const deleteEquipe = async (equipeId) => {
    try {
      const res = await fetch(`${API}/api/operation/equipes/${equipeId}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success("Équipe supprimée");
        return true;
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur suppression équipe");
        return false;
      }
    } catch (err) {
      toast.error("Erreur suppression équipe");
      return false;
    }
  };

  // Modifier une équipe via l'API
  const updateEquipe = async (equipeId, equipeData) => {
    try {
      const params = new URLSearchParams();
      if (equipeData.label) params.append('nom', equipeData.label);
      if (equipeData.description !== undefined) params.append('description', equipeData.description);
      if (equipeData.color) params.append('couleur', classToHex(equipeData.color));
      
      const res = await fetch(`${API}/api/operation/equipes/${equipeId}?${params.toString()}`, {
        method: "PUT",
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success("Équipe modifiée");
        return true;
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur modification équipe");
        return false;
      }
    } catch (err) {
      toast.error("Erreur modification équipe");
      return false;
    }
  };

  const handleAddItem = async () => {
    if (!newItem.label) {
      toast.error("Libellé obligatoire");
      return;
    }

    // Pour les équipes, utiliser l'API dédiée
    if (activeListe === 'equipes') {
      setSaving(true);
      const newId = await addEquipe(newItem);
      if (newId) {
        const currentItems = listes[activeListe] || [];
        const newEquipe = {
          id: newId,
          code: newItem.label.toLowerCase().replace(/\s+/g, '_').replace(/[éèê]/g, 'e'),
          label: newItem.label,
          color: newItem.color,
          description: newItem.description || ""
        };
        setListes(prev => ({ ...prev, equipes: [...currentItems, newEquipe] }));
      }
      setSaving(false);
      setNewItem({ code: "", label: "", color: "bg-zinc-500/20 border border-[var(--oh-border)]", description: "" });
      setShowAddDialog(false);
      return;
    }

    // Pour les autres listes
    if (!newItem.code) {
      toast.error("Code obligatoire");
      return;
    }

    // Vérifier que le code n'existe pas déjà
    const currentItems = listes[activeListe] || [];
    if (currentItems.some(item => item.code === newItem.code)) {
      toast.error("Ce code existe déjà");
      return;
    }

    const updatedItems = [...currentItems, { ...newItem }];
    saveListe(activeListe, updatedItems);
    setNewItem({ code: "", label: "", color: "bg-zinc-500/20 border border-[var(--oh-border)]", description: "" });
    setShowAddDialog(false);
  };

  const handleEditItem = (item) => {
    setEditingItem({ ...item });
    setNewItem({ ...item });
    setShowAddDialog(true);
  };

  const handleUpdateItem = async () => {
    if (!newItem.label) {
      toast.error("Libellé obligatoire");
      return;
    }

    // Pour les équipes, utiliser l'API dédiée
    if (activeListe === 'equipes' && editingItem?.id) {
      setSaving(true);
      const success = await updateEquipe(editingItem.id, newItem);
      if (success) {
        const currentItems = listes[activeListe] || [];
        const updatedItems = currentItems.map(item =>
          item.id === editingItem.id ? { ...item, ...newItem } : item
        );
        setListes(prev => ({ ...prev, equipes: updatedItems }));
      }
      setSaving(false);
      setEditingItem(null);
      setNewItem({ code: "", label: "", color: "bg-zinc-500/20 border border-[var(--oh-border)]", description: "" });
      setShowAddDialog(false);
      return;
    }

    // Pour les autres listes
    if (!newItem.code) {
      toast.error("Code obligatoire");
      return;
    }

    const currentItems = listes[activeListe] || [];
    const updatedItems = currentItems.map(item =>
      item.code === editingItem.code ? { ...newItem } : item
    );
    saveListe(activeListe, updatedItems);
    setEditingItem(null);
    setNewItem({ code: "", label: "", color: "bg-zinc-500/20 border border-[var(--oh-border)]", description: "" });
    setShowAddDialog(false);
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Supprimer "${item.label}" ?`)) return;

    // Pour les équipes, utiliser l'API dédiée
    if (activeListe === 'equipes' && item.id) {
      setSaving(true);
      const success = await deleteEquipe(item.id);
      if (success) {
        const currentItems = listes[activeListe] || [];
        const updatedItems = currentItems.filter(i => i.id !== item.id);
        setListes(prev => ({ ...prev, equipes: updatedItems }));
      }
      setSaving(false);
      return;
    }

    // Pour les autres listes
    const currentItems = listes[activeListe] || [];
    const updatedItems = currentItems.filter(i => i.code !== item.code);
    saveListe(activeListe, updatedItems);
  };

  const currentItems = listes[activeListe] || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[var(--oh-text-muted)] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-medium text-[var(--oh-text)] flex items-center gap-2">
            <List className="w-6 h-6 text-cyan-400" />
            Configuration des Listes Métier
          </h2>
          <p className="text-[var(--oh-text-muted)] text-sm mt-1">
            Gérez les valeurs des listes déroulantes de l'application
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingItem(null);
            setNewItem({ code: "", label: "", color: "bg-zinc-500/20 border border-[var(--oh-border)]", description: "" });
            setShowAddDialog(true);
          }}
          className="bg-green-600 hover:bg-green-700"
          data-testid="add-list-item-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une valeur
        </Button>
      </div>

      {/* Tabs pour les différentes listes */}
      <Tabs value={activeListe} onValueChange={setActiveListe}>
        <TabsList className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] flex-wrap h-auto gap-1 p-1">
          {Object.entries(LISTES_CONFIG).map(([code, config]) => (
            <TabsTrigger
              key={code}
              value={code}
              className="data-[state=active]:bg-cyan-600 text-sm"
            >
              <span className="mr-1">{config.icon}</span>
              {config.nom}
              <Badge className="ml-2 bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)] text-xs">
                {(listes[code] || []).length}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.entries(LISTES_CONFIG).map(([code, config]) => (
          <TabsContent key={code} value={code} className="mt-4">
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader>
                <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
                  <span>{config.icon}</span>
                  {config.nom}
                </CardTitle>
                <CardDescription className="text-[var(--oh-text-muted)]">
                  {config.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(listes[code] || []).length === 0 ? (
                  <div className="text-center py-8 text-[var(--oh-text-muted)]">
                    <List className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Aucune valeur configurée</p>
                    <p className="text-sm">Cliquez sur "Ajouter une valeur" pour commencer</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(listes[code] || []).map((item, idx) => (
                      <div
                        key={item.id || item.code}
                        className="flex items-center gap-4 p-3 bg-[var(--oh-bg-surface)] rounded-lg group"
                        data-testid={`list-item-${item.code || item.id}`}
                      >
                        <div className="text-[var(--oh-text-secondary)] text-sm w-6">{idx + 1}</div>
                        
                        <div className={`w-4 h-4 rounded-full ${item.color || 'bg-[var(--oh-bg-elevated)]'}`} />
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[var(--oh-text)] font-medium">{item.label}</span>
                            {/* Masquer le code pour les équipes */}
                            {code !== 'equipes' && (
                              <Badge className="bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)] text-xs font-mono">
                                {item.code}
                              </Badge>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-[var(--oh-text-muted)] text-xs mt-1">{item.description}</p>
                          )}
                        </div>

                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditItem(item)}
                            className="text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteItem(item)}
                            className="text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)]"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Dialog Ajout/Édition */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">
              {editingItem 
                ? (activeListe === 'equipes' ? "Modifier l'équipe" : "Modifier la valeur")
                : (activeListe === 'equipes' ? "Ajouter une équipe" : "Ajouter une valeur")
              }
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Pour les équipes : juste le nom. Pour les autres : code + libellé */}
            {activeListe === 'equipes' ? (
              <div>
                <Label className="text-[var(--oh-text-muted)]">Nom de l'équipe *</Label>
                <Input
                  value={newItem.label}
                  onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
                  placeholder="ex: Équipe Pose PAC"
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[var(--oh-text-muted)]">Code technique *</Label>
                  <Input
                    value={newItem.code}
                    onChange={(e) => setNewItem({ 
                      ...newItem, 
                      code: e.target.value.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "")
                    })}
                    placeholder="ex: en_cours"
                    disabled={!!editingItem}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] font-mono"
                  />
                  <p className="text-xs text-[var(--oh-text-secondary)] mt-1">Lettres minuscules et underscores</p>
                </div>
                <div>
                  <Label className="text-[var(--oh-text-muted)]">Libellé affiché *</Label>
                  <Input
                    value={newItem.label}
                    onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
                    placeholder="ex: En cours"
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
              </div>
            )}

            <div>
              <Label className="text-[var(--oh-text-muted)]">Couleur</Label>
              <Select
                value={newItem.color}
                onValueChange={(v) => setNewItem({ ...newItem, color: v })}
              >
                <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COULEURS_DISPONIBLES.map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full ${c.code}`} />
                        {c.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[var(--oh-text-muted)]">Description (optionnel)</Label>
              <Input
                value={newItem.description || ""}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Description courte..."
                className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
              />
            </div>

            {/* Prévisualisation */}
            <div className="p-3 bg-[var(--oh-bg-surface)] rounded-lg">
              <Label className="text-[var(--oh-text-muted)] text-xs">Prévisualisation</Label>
              <div className="flex items-center gap-3 mt-2">
                <div className={`w-4 h-4 rounded-full ${newItem.color}`} />
                <span className="text-[var(--oh-text)]">{newItem.label || (activeListe === 'equipes' ? "Nom équipe" : "Libellé")}</span>
                {activeListe !== 'equipes' && (
                  <Badge className="bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)] text-xs font-mono">
                    {newItem.code || "code"}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddDialog(false);
              setEditingItem(null);
            }}>
              Annuler
            </Button>
            <Button
              onClick={editingItem ? handleUpdateItem : handleAddItem}
              className="bg-cyan-600 hover:bg-cyan-700"
              disabled={saving}
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingItem ? "Mettre à jour" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
